import * as React from 'react';
import { BaSeButton, ButtonProps } from '../button/button';
import {
  BaSeShapeButton,
  ShapeButtonProps,
} from '../shape-button/shape-button';
import {
  ButtonWrapper,
  Container,
  StyledButtonGroupAlign,
  StyledButtonGroupBorderRadius,
  StyledButtonGroupDisposition,
  StyledButtonGroupType,
} from './button-group-styled';

export interface ButtonGroupProps {
  type: StyledButtonGroupType;
  items: ButtonProps[] | ShapeButtonProps[];
  gap?: number;
  selected?: number | number[];
  floatLabels?: string[];
  align?: StyledButtonGroupAlign;
  disposition?: StyledButtonGroupDisposition;
  borderRadius?: StyledButtonGroupBorderRadius;
  withoutElevation?: boolean;
  onChange?: (selected: number | number[]) => void;
}

export const BaSeButtonGroup: React.FC<ButtonGroupProps> = ({
  type,
  items,
  selected,
  floatLabels,
  borderRadius,
  gap = 0,
  align = 'start',
  disposition = 'horizontal',
  withoutElevation,
  onChange,
}) => {
  if (floatLabels && floatLabels.length > items.length - 1) {
    throw Error(
      '[ButtonGroupProps] error: Informe no máximo a quantidade de ("items" - 1) para os "floatLabels"!',
    );
  }

  const [internalSelected, setInternalSelected] = React.useState<
    number | number[]
  >(selected ?? (type === 'checkbox' ? [] : -1));

  const isSelected = React.useCallback(
    (index: number): boolean => {
      if (type === 'checkbox') {
        return Array.isArray(internalSelected)
          ? internalSelected.includes(index)
          : false;
      }
      return internalSelected === index;
    },
    [internalSelected, type],
  );

  const typeBySelected = React.useCallback(
    (item: ButtonProps | ShapeButtonProps, index: number) => {
      if (item?.type === 'tertiary') {
        throw new Error(
          '[ButtonGroupProps] error: Tipo "tertiary" não suportado.',
        );
      }

      if (type === 'button') {
        return item.type;
      }

      return isSelected(index) ? 'primary' : 'secondary';
    },
    [isSelected, type],
  );

  const handleClick = React.useCallback(
    (
      index: number,
      item: ButtonProps | ShapeButtonProps,
      event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    ) => {
      if (type === 'button') {
        onChange?.(index);
      }

      if (type === 'radio') {
        setInternalSelected(index);
        onChange?.(index);
      }

      if (type === 'checkbox') {
        const current = Array.isArray(internalSelected)
          ? [...internalSelected]
          : [];

        const exists = current.includes(index);
        const newSelected = exists
          ? current.filter((i) => i !== index)
          : [...current, index];

        setInternalSelected(newSelected);
        onChange?.(newSelected);
      }
      return item.onClick?.(event);
    },
    [internalSelected, onChange, type],
  );

  React.useEffect(() => {
    if (selected !== undefined) {
      setInternalSelected(selected);
    }
  }, [selected]);

  React.useEffect(() => {
    if (selected === undefined) return;
    const arr = Array.isArray(selected) ? selected : [selected];
    const hasInvalid = arr.some((i) => i < 0 || i >= items.length);
    if (hasInvalid) {
      throw new Error(
        '[ButtonGroupProps] error: "selected" contém índices inválidos.',
      );
    }
  }, [selected, items.length]);

  return (
    <Container align={align} gap={gap} disposition={disposition}>
      {items.map((item, index) => (
        <ButtonWrapper
          key={index}
          gap={gap}
          firstItem={index === 0}
          lastItem={index === items.length - 1}
          disposition={disposition}
          borderRadius={borderRadius}
          floatLabel={floatLabels?.[index]}
          withoutElevation={withoutElevation}
        >
          {'nameIcon' in item ? (
            <BaSeShapeButton
              {...item}
              type={typeBySelected(item, index)}
              onClick={(event) => handleClick(index, item, event)}
            />
          ) : (
            <BaSeButton
              {...(item as ButtonProps)}
              type={typeBySelected(item, index)}
              onClick={(event) => handleClick(index, item, event)}
            />
          )}
        </ButtonWrapper>
      ))}
    </Container>
  );
};

BaSeButtonGroup.displayName = 'BaSeButtonGroup';
